import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import styles from './make-lists.module.css';

const MakeLists = ({ jobId, applications, listName, setListName, selectedList, setSelectedList, selectedApplicants, setSelectedApplicants }) => {
    
    const [lists, setLists] = useState([]);
    const [notification, setNotification] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`/api/recruiter/lists/all/${jobId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setLists(res.data.lists);
            } catch (error) {
                const msg = error.response?.data?.message || 'Error loading data';
                setNotification(msg);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [jobId]);

    const handleListNameChange = useCallback((e) => {
        setListName(e.target.value);
    }, []);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, [selectedList]);

    const handleSaveList = async () => {
        if (selectedApplicants.length === 0) {
            setNotification('Please select at least one applicant');
            return;
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const endpoint = selectedList ? '/api/recruiter/lists/update' : '/api/recruiter/lists/create';
            const payload = selectedList 
                ? { jobId, listId: selectedList._id, name: listName, applicantIds: selectedApplicants }
                : { jobId, name: listName, applicantIds: selectedApplicants };

            const response = await axios({
                method: selectedList ? 'put' : 'post',
                url: endpoint,
                data: payload,
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setNotification(selectedList ? 'List updated successfully' : 'List created successfully');
            if (selectedList) {
                setLists(lists.map(list => list._id === selectedList._id ? response.data.list : list));
            } else {
                setLists([...lists, response.data.list]);
            }

            setSelectedList(null);
            setListName('');
            setSelectedApplicants([]);
        } catch (error) {
            setNotification(error.response?.data?.message || 'Error saving list');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteList = async () => {
        if (!selectedList) return;
        if (!window.confirm(`Are you sure you want to delete the list "${selectedList.name}"?`)) return;

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete('/api/recruiter/lists/delete', {
                data: { jobId, listId: selectedList._id },
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setLists(lists.filter(list => list._id !== selectedList._id));
            setNotification('List deleted successfully');
            setSelectedList(null);
            setListName('');
            setSelectedApplicants([]);
        } catch (error) {
            setNotification(error.response?.data?.message || 'Error deleting list');
        } finally {
            setIsLoading(false);
        }
    };

    const handleApplicantSelect = (applicantId) => {
    setSelectedApplicants(prev => {
        // Ensure prev is always an array
        const current = Array.isArray(prev) ? prev : [];
        return current.includes(applicantId)
        ? current.filter(id => id !== applicantId)
        : [...current, applicantId];
    });
    };

    const handleListSelect = (list) => {
        setSelectedList(list);
        setListName(list.name);
        setSelectedApplicants(list.applicants.map(applicant => applicant._id));
    };

    return (
        <div className={styles.makeListsContainer}>
            {notification && (
                <div className={`${styles.notification} ${notification.includes('Error') ? styles.error : ''}`}>
                    {notification}
                </div>
            )}

            <div className={styles.listCreationSection}>
                <div className={styles.header}>
                    <h2>{selectedList ? 'Edit List' : 'Create New List'}</h2>
                    {selectedList && (
                        <button className={styles.closeButton} onClick={() => {
                            setSelectedList(null);
                            setListName('');
                            setSelectedApplicants([]);
                        }}>
                            X
                        </button>
                    )}
                </div>

                <div className={styles.listControls}>
                    <div className={styles.inputContainer}>
                        <label>List Name</label>
                        <input
                            ref={inputRef}
                            type="text"
                            value={listName}
                            onChange={handleListNameChange}
                            placeholder="Enter list name"
                            className={styles.listNameInput}
                        />
                    </div>
                </div>

                <div className={styles.applicantsSection}>
                    <div className={styles.sectionHeader}>
                        <h3>Select Applicants ({selectedApplicants.length} selected)</h3>
                        <div className={styles.selectActions}>
                            <button 
                                onClick={() => setSelectedApplicants(applications.map(app => String(app.applicantId?._id)).filter(Boolean))}
                                className={styles.selectAllButton}
                            >
                                Select All
                            </button>
                            <button 
                                onClick={() => setSelectedApplicants([])}
                                className={styles.clearAllButton}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                    <div className={styles.applicantList}>
                        {applications.map(app => {
                            const id = String(app.applicantId?._id);
                            const name = app.applicantId?.name || 'Unnamed';
                            const isSelected = Array.isArray(selectedApplicants) && selectedApplicants.includes(id);
                            return (
                                
                                // <div 
                                //     key={id} 
                                //     className={styles.applicantItem}
                                //     onClick={() => {
                                //         setSelectedApplicants(prev => {
                                //             // Ensure prev is always treated as an array
                                //             const current = Array.isArray(prev) ? prev : [];
                                //             return current.includes(id)
                                //                 ? current.filter(pid => pid !== id)
                                //                 : [...current, id]
                                //         });
                                //     }}
                                // >
                                //     <input
                                //         type="checkbox"
                                //         checked={Array.isArray(selectedApplicants) && selectedApplicants.includes(id)}
                                //         onChange={() => {}}
                                //         readOnly
                                //     />
                                //     <span>{name}</span>
                                // </div>
                                <>
                                <div 
                                    key={id} 
                                    className={styles.applicantItem}
                                    onClick={(e) => {
                                    // Only trigger if the click wasn't directly on the checkbox
                                    if (e.target.type !== 'checkbox') {
                                        handleApplicantSelect(id);
                                    }
                                    }}
                                >
                                    <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleApplicantSelect(id)}
                                    />
                                    <span>{name}</span>
                                </div>
                                </>
                            );
                        })}
                    </div>
                </div>

                <div className={styles.listActions}>
                    {selectedList && (
                        <button 
                            onClick={handleDeleteList}
                            className={styles.deleteButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Deleting...' : 'Delete List'}
                        </button>
                    )}
                    <button 
                        onClick={handleSaveList} 
                        className={styles.saveButton}
                        disabled={isLoading || selectedApplicants.length === 0}
                    >
                        {isLoading ? 'Saving' : selectedList ? 'Update List' : 'Create List'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MakeLists;
